import { BoughtCouponsPackBase } from "../json-schemas/base/bought_coupons_pack"
import { CouponPackBase } from "../json-schemas/base/coupon_pack"
import { CustomerCouponsPackNewResponse } from "../json-schemas/serializers/customer-coupons-pack-new-response"
import { httpGet, httpGetRoute, httpPost } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import { couponsPackI18nValidityDurationInMonths } from "./coupons-packs.service"
import { moment } from "../utils/moment.service"
import { CustomerBoughtCouponsPacksIndexProps } from "../json-schemas/serializers/customer-bought-coupons-packs-index-props"
import { BackofficeBoughtCouponsPacksIndexResponse } from "../json-schemas/serializers/backoffice-bought-coupons-packs-index-response"
import i18nScopedData from './bought-coupons-packs.service.yml'
import { CustomerBase } from "../json-schemas/base/customer"
import { BackofficeBoughtCouponsPacksCountPerScopeResponse } from "../json-schemas/serializers/backoffice-bought-coupons-packs-count-per-scope-response"

const i18nScoped = new I18nService(i18nScopedData)

export const boughtCouponsPackSorts = [
  'paid_at asc', 'paid_at desc',
  'created_at asc', 'created_at desc',
  'expires_at asc', 'expires_at desc'
] as const

export type BoughtCouponsPackSort = typeof boughtCouponsPackSorts[number]

export interface BackofficeRansackQuery {
  'q[id_not_in][]'?: number[]
  'q[coupons_pack_id_eq]'?: number
  'q[paid_at_not_null]'?: boolean
  'q[generated_counter_or_customer_sortable_name_or_customer_reversed_sortable_name_cont]'?: string
  scope?: 'paid_not_expired' | 'unpaid' | 'expired' | 'all'
  'q[s]'?: BoughtCouponsPackSort
}

export const boughtCouponsPacksScopes = ['paid_not_expired', 'unpaid', 'expired', 'all'] satisfies BackofficeRansackQuery['scope'][]

export function boughtCouponsPackList (query: BackofficeRansackQuery = {}): Promise<BackofficeBoughtCouponsPacksIndexResponse> {
  return httpGet(`/${httpGetRoute()}/bought_coupons_packs`, query)
}

export function boughtCouponsPackCountPerScope (query: BackofficeRansackQuery = {}): Promise<BackofficeBoughtCouponsPacksCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/bought_coupons_packs/count_per_scope`, query)
}

export function boughtCouponsPackNew (bought_coupons_pack_id: BoughtCouponsPackBase['id'], customerId: CustomerBase['id']): Promise<CustomerCouponsPackNewResponse> {
  if (window.currentUser.type === 'Operator') return httpGet(`/${httpGetRoute()}/customers/${customerId}/bought_coupons_packs/new`, { bought_coupons_pack_id })
  return httpGet(`/${httpGetRoute()}/bought_coupons_packs/new`, { bought_coupons_pack_id })
}

export function boughtCouponsPackCreate (coupons_pack_id: CouponPackBase['id'], customerId: CustomerBase['id'], skip_trigger_payment_invoice: boolean): Promise<{ redirect_url: string }> {
  if (window.currentUser.type === 'Operator') return httpPost(`/${httpGetRoute()}/customers/${customerId}/bought_coupons_packs`, { bought_coupons_pack: { coupons_pack_id, skip_trigger_payment_invoice } })
  return httpPost(`/${httpGetRoute()}/bought_coupons_packs`, { bought_coupons_pack: { coupons_pack_id } })
}

export function boughtCouponsPackI18nValidUntil (createdAt: string, validityDurationInMonths: number | null) {
  const formattedValidityDuration = couponsPackI18nValidityDurationInMonths(validityDurationInMonths)
  if (!validityDurationInMonths) return formattedValidityDuration
  return i18nScoped.t('valid_until', { validity_duration: formattedValidityDuration, start: moment(createdAt).format('ll') })
}

export function boughtCouponsPackI18nScope (scope: BackofficeRansackQuery['scope']) {
  return i18nScoped.t(`scope.${scope}`)
}

export function boughtCouponsPackConsumptionInsights (
  quantityLeftForBoughtCouponsPacks: CustomerBoughtCouponsPacksIndexProps['customerCouponsPacksSimulationResult']['quantityLeftForBoughtCouponsPacks'],
  boughtCouponsPack: Pick<BoughtCouponsPackBase, 'id' | 'get_quantity_left'> & { coupons_pack: Pick<CouponPackBase, 'quantity'> }
) {
  const foundQuantityLeft = quantityLeftForBoughtCouponsPacks.find(_ => _.bought_coupons_pack.id === boughtCouponsPack.id)?.quantity_left

  /**
   * `quantityLeftForBoughtCouponsPacks.quantity_left` can be negative because customer#simulate_coupons_consumption
   * tries to consume bought coupons packs before giving up if there is no enough quantity.
   * 
   * If `foundQuantityLeft` is undefined, this bought coupons packs is not yet used and therefore 
   * it is full of `coupons_pack.quantity` hours.
   */
  const simulatedIncomingQuantityLeft = foundQuantityLeft ?? boughtCouponsPack.get_quantity_left

  /**
   * The duration that is planned, but not yet consumed.
   * This includes both the duration for the incomming week and the duration for past event that did not yet go through the invoicing task (not having event.coupons yet).
   */
  const simulatedIncomingQuantityPlanned = boughtCouponsPack.get_quantity_left - simulatedIncomingQuantityLeft
  
  /**
   * The duration of events that did already go through the invoicing task (events.coupons generated).
   * When used, we should consider computing volume of events not yet processed (events.coupons being nil).
   */
  const quantityUsed = boughtCouponsPack.coupons_pack.quantity - boughtCouponsPack.get_quantity_left
  const simulatedQuantityLeft = boughtCouponsPack.coupons_pack.quantity - simulatedIncomingQuantityPlanned - quantityUsed

  return { simulatedIncomingQuantityPlanned, quantityUsed, simulatedQuantityLeft }
}
